import { useState, useEffect } from 'react';
import { configService } from '@/lib/config-service';
import { useAuth } from '@/providers/AuthProvider';
import { useCompany } from '@/contexts/CompanyContext';

interface EvoFeedbackUrlParams {
  feature: string;
  customerName?: string;
  userId?: string;
  reDisplayAfterDays?: number;
}

/**
 * Custom hook to build the Evo feedback collector URL with all required parameters.
 * Automatically sources user and customer context from AuthProvider and CompanyContext.
 * Explicit prop values override the auto-detected context values.
 *
 * Only the parameters accepted by the evo-feature-feedback-collector OC component
 * are included: product, feature, accountToken, customerName, userId.
 * Optional reDisplayAfterDays is appended when explicitly provided.
 *
 * @param params - The parameters for the Evo feedback collector
 * @returns The complete URL for the oc-component, or null if config is not loaded yet
 */
export function useEvoFeedbackCollectorUrl(params: EvoFeedbackUrlParams): string | null {
  const { feature, customerName, userId, reDisplayAfterDays } = params;
  const { authenticatedUser, instanceId } = useAuth();
  const { currentCompany } = useCompany();
  const [url, setUrl] = useState<string | null>(null);

  const resolvedCustomerName = customerName ?? currentCompany?.name ?? instanceId ?? 'Unknown';
  const resolvedUserId = userId ?? authenticatedUser?.email ?? 'Unknown';

  useEffect(() => {
    let isMounted = true;

    async function buildUrl(): Promise<void> {
      try {
        const config = await configService.getFeedbackCollectorConfig();

        if (!isMounted) return;

        if (!config.accountToken) {
          console.error('Feedback collector: accountToken is missing, component will not render');
          setUrl(null);
          return;
        }

        const urlParams = new URLSearchParams();
        urlParams.set('product', config.product);
        urlParams.set('feature', feature);
        urlParams.set('accountToken', config.accountToken);
        urlParams.set('customerName', resolvedCustomerName);
        urlParams.set('userId', resolvedUserId);

        if (reDisplayAfterDays !== undefined) {
          urlParams.set('reDisplayAfterDays', String(reDisplayAfterDays));
        }

        const fullUrl = `${config.baseUrl}?${urlParams.toString()}`;
        setUrl(fullUrl);
      } catch (error) {
        console.error('Failed to build feedback collector URL:', error);
        setUrl(null);
      }
    }

    buildUrl();

    return () => {
      isMounted = false;
    };
  }, [feature, resolvedCustomerName, resolvedUserId, reDisplayAfterDays]);

  return url;
}
